import React, { useCallback, useEffect, useRef, useState } from "react";
import { Crosshair } from "lucide-react";

const NEEDED = 3;
const newBand = () => ({ start: 10 + Math.random() * 68, width: 14 });

export const VitalsGame = ({ onWin, onPenalty }) => {
  const needleRef = useRef(null);
  const pos = useRef(0);
  const dir = useRef(1);
  const speed = useRef(0.054);
  const doneRef = useRef(false);
  const [band, setBand] = useState(newBand);
  const [hits, setHits] = useState(0);
  const [miss, setMiss] = useState(false);

  useEffect(() => {
    let raf;
    let prev = performance.now();
    const step = (now) => {
      const dt = Math.min(50, now - prev);
      prev = now;
      pos.current += dir.current * speed.current * dt;
      if (pos.current >= 100) { pos.current = 100; dir.current = -1; }
      if (pos.current <= 0) { pos.current = 0; dir.current = 1; }
      if (needleRef.current) needleRef.current.style.left = `${pos.current}%`;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const lock = useCallback(() => {
    if (doneRef.current) return;
    const p = pos.current;
    if (p >= band.start && p <= band.start + band.width) {
      const n = hits + 1;
      setHits(n);
      speed.current *= 1.35;
      if (n >= NEEDED) {
        doneRef.current = true;
        onWin();
      } else {
        setBand(newBand());
      }
    } else {
      setMiss(true);
      setTimeout(() => setMiss(false), 300);
      onPenalty(3);
    }
  }, [band, hits, onWin, onPenalty]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        if (!e.repeat) lock();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lock]);

  return (
    <div className="space-y-6" data-testid="vitals-game">
      <div className="flex items-center justify-between font-tac text-sm">
        <span className="text-slate-400">VITALS LOCKED</span>
        <div className="flex gap-2" data-testid="vitals-hit-count" data-hits={hits}>
          {Array.from({ length: NEEDED }).map((_, i) => (
            <span key={i} className={`w-3 h-3 rounded-full border ${i < hits ? "bg-emerald-400 border-emerald-300" : "border-slate-600"}`} />
          ))}
        </div>
      </div>
      <div className={`relative h-28 rounded-sm border bg-slate-950 spore-grid-bg overflow-hidden ${miss ? "border-red-500 spore-shake" : "border-slate-800"}`}>
        <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 400 100" preserveAspectRatio="none" aria-hidden="true">
          <polyline fill="none" stroke="#FF2E63" strokeWidth="1.5" points="0,50 60,50 75,20 90,80 105,50 180,50 195,15 210,85 225,50 300,50 315,25 330,75 345,50 400,50" />
        </svg>
        <div
          data-testid="vitals-band"
          data-start={band.start}
          data-width={band.width}
          className="absolute inset-y-0 bg-emerald-500/25 border-x-2 border-emerald-400 transition-[left] duration-300"
          style={{ left: `${band.start}%`, width: `${band.width}%` }}
        />
        <div ref={needleRef} data-testid="vitals-needle" className="absolute inset-y-0 w-[3px] -ml-[1.5px] bg-cyan-300 shadow-[0_0_12px_rgba(0,245,255,0.9)]" />
      </div>
      <button
        type="button"
        data-testid="vitals-lock-btn"
        onPointerDown={lock}
        onKeyDown={(e) => e.key === "Enter" && !e.repeat && lock()}
        className="w-full py-4 rounded-sm border border-emerald-500/60 bg-emerald-950/40 text-emerald-200 font-display uppercase tracking-[0.3em] font-bold hover:bg-emerald-900/50 active:scale-[0.99] transition-[background-color,transform] duration-150 flex items-center justify-center gap-3"
      >
        <Crosshair className="h-5 w-5" /> Lock
      </button>
    </div>
  );
};
